import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, X, Send, Loader2, LifeBuoy, Mail, ChevronLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'bc_support_session';

const getSessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

export const SupportChat = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'fr').slice(0, 2);
  const [open, setOpen] = useState(false);
  const [faq, setFaq] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('chat');
  const [contact, setContact] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/support/faq`, { params: { lang }, withCredentials: true }).then((res) => {
      setFaq(res.data.faq || []);
      setMessages((prev) => (prev.length > 1 ? prev : [{ role: 'assistant', content: res.data.greeting }]));
    }).catch(() => {});
  }, [open, user, lang]);

  useEffect(() => {
    setContact((c) => ({ ...c, name: c.name || user?.company_name || '', email: c.email || user?.email || '' }));
  }, [user]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, mode, open]);

  const ask = async (text) => {
    const question = text.trim();
    if (!question || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: question }]);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/support/chat`, { session_id: getSessionId(), message: question, lang }, { withCredentials: true });
      setMessages((m) => [...m, { role: 'assistant', content: res.data.reply, needs_human: res.data.needs_human }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: t('support.error'), needs_human: true }]);
    } finally {
      setBusy(false);
    }
  };

  const openContact = () => {
    const lastQuestion = [...messages].reverse().find((m) => m.role === 'user')?.content || '';
    setContact((c) => ({ ...c, message: c.message || lastQuestion }));
    setMode('contact');
  };

  const sendContact = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/support/contact`, { ...contact, session_id: getSessionId() }, { withCredentials: true });
      setSent(true);
      toast.success("Message transmis à l'administrateur");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Erreur lors de l'envoi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-[70] w-14 h-14 rounded-full bg-[#1E3A8A] text-white shadow-2xl flex items-center justify-center hover:scale-105 transition-transform"
          aria-label={t('support.help')}
          data-testid="support-chat-bubble"
        >
          <MessageCircle className="w-7 h-7" />
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#F59E0B] border-2 border-white" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-20 md:bottom-6 right-2 md:right-6 z-[70] w-[calc(100vw-1rem)] sm:w-[380px] h-[min(560px,calc(100vh-7rem))] flex flex-col rounded-2xl bg-white shadow-2xl border border-[#1E3A8A]/10 overflow-hidden" data-testid="support-chat-panel">
          <div className="bg-[#1E3A8A] text-white px-4 py-3 flex items-center gap-3">
            {mode === 'contact' ? (
              <button onClick={() => setMode('chat')} className="p-1 rounded hover:bg-white/10" aria-label="Retour" data-testid="support-back"><ChevronLeft className="w-5 h-5" /></button>
            ) : (
              <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center"><LifeBuoy className="w-5 h-5" /></span>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-sm leading-tight">{mode === 'contact' ? t('support.contactTitle') : t('support.title')}</p>
              <p className="text-[11px] text-blue-100">{mode === 'contact' ? t('support.contactSubtitle') : t('support.subtitle')}</p>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/10" aria-label="Fermer" data-testid="support-chat-close"><X className="w-5 h-5" /></button>
          </div>

          {mode === 'chat' ? (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F9FAFB]" data-testid="support-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-line leading-relaxed ${m.role === 'user' ? 'bg-[#1E3A8A] text-white rounded-br-sm' : 'bg-white border border-[#E5E7EB] text-[#111827] rounded-bl-sm'}`} data-testid={`support-msg-${m.role}`}>
                      {m.content}
                      {m.needs_human && (
                        <button onClick={openContact} className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[#F59E0B] hover:underline" data-testid="support-escalate-btn">
                          <Mail className="w-3.5 h-3.5" /> {t('support.escalate')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {busy && <div className="flex items-center gap-2 text-xs text-[#6B7280]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('support.typing')}</div>}
                {messages.length <= 1 && faq.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1" data-testid="support-faq-chips">
                    {faq.map((f) => (
                      <button key={f.id} onClick={() => ask(f.question)} className="text-xs px-3 py-1.5 rounded-full border border-[#1E3A8A]/30 text-[#1E3A8A] bg-white hover:bg-[#1E3A8A]/5 text-left" data-testid={`support-faq-${f.id}`}>
                        {f.question}
                      </button>
                    ))}
                  </div>
                )}
                <div ref={endRef} />
              </div>
              <form onSubmit={(e) => { e.preventDefault(); ask(input); }} className="p-3 border-t border-[#E5E7EB] bg-white flex items-center gap-2">
                <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t('support.placeholder')} className="input-field flex-1" data-testid="support-chat-input" />
                <Button type="submit" size="icon" className="bg-[#1E3A8A] flex-shrink-0" disabled={busy || !input.trim()} data-testid="support-chat-send"><Send className="w-4 h-4" /></Button>
              </form>
              <button onClick={openContact} className="text-[11px] text-[#6B7280] hover:text-[#1E3A8A] pb-2 bg-white" data-testid="support-contact-link">{t('support.contactLink')}</button>
            </>
          ) : sent ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-testid="support-contact-sent">
              <span className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4"><Mail className="w-7 h-7" /></span>
              <p className="font-heading font-bold text-[#111827]">{t('support.sentTitle')}</p>
              <p className="text-sm text-[#4B5563] mt-2">{t('support.sentBody')} <strong>{contact.email}</strong>.</p>
              <Button variant="outline" className="mt-5" onClick={() => { setSent(false); setMode('chat'); setContact((c) => ({ ...c, message: '' })); }}>{t('support.backToChat')}</Button>
            </div>
          ) : (
            <form onSubmit={sendContact} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F9FAFB]" data-testid="support-contact-form">
              <p className="text-xs text-[#4B5563]">{t('support.contactIntro')}</p>
              <Input required placeholder={t('support.name')} value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="input-field" data-testid="support-contact-name" />
              <Input required type="email" placeholder={t('support.email')} value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="input-field" data-testid="support-contact-email" />
              <Textarea required rows={5} placeholder={t('support.message')} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} className="input-field" data-testid="support-contact-message" />
              <Button type="submit" className="w-full btn-accent" disabled={busy} data-testid="support-contact-submit">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} {t('support.send')}
              </Button>
            </form>
          )}
        </div>
      )}
    </>
  );
};
